import { useTranslation } from "react-i18next";
import type { LabResultsListProps } from "../../types";
import { LabResultItem } from "../../molecules/LabResultItem";
import { LabStatusFilterBar } from "../../molecules/LabStatusFilterBar";
import { LabScreeningDateNav } from "../../molecules/LabScreeningDateNav";
import { useLabResultsList } from "./useLabResultsList";
import { cn } from "../../../../lib/utils";

export const LabResultsList = ({
  results,
  selectedId,
  onSelect,
  className,
  screeningDates,
  selectedDate,
  onDateChange,
}: LabResultsListProps) => {
  const { t } = useTranslation("ui");
  const {
    filteredResults,
    dateFilteredResults,
    activeFilters,
    setActiveFilters,
    formattedScreeningDate,
    totalTests,
    readingForResult,
    getReading,
  } = useLabResultsList({
    results,
    selectedId,
    selectedDate,
  });

  const hasDateNav = screeningDates && screeningDates.length > 0 && selectedDate && onDateChange;

  return (
    <div data-slot="lab-results-list" className={cn("flex flex-col h-full", className)}>
      {/* Fixed header */}
      <div className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t("Lab Results")}</h2>
          {hasDateNav ? (
            <LabScreeningDateNav dates={screeningDates} selectedDate={selectedDate} onDateChange={onDateChange} />
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {formattedScreeningDate && <span>{t("{{date}} Screening", { date: formattedScreeningDate })}</span>}
              {formattedScreeningDate && <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>}
              {t("{{count}} tests", { count: totalTests })}
            </p>
          )}
        </div>
        <LabStatusFilterBar
          results={dateFilteredResults}
          activeFilters={activeFilters}
          onFilterChange={setActiveFilters}
          getReading={getReading}
        />
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {results.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t("No lab results available")}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs leading-relaxed">
              {t("Lab results will appear here once they become available from your healthcare provider.")}
            </p>
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <svg
                className="w-6 h-6 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t("No results match this filter")}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t("Try adjusting your filter selection.")}</p>
          </div>
        ) : (
          filteredResults.map((result) => (
            <LabResultItem
              key={result.id}
              result={result}
              selected={result.id === selectedId}
              displayReading={readingForResult(result)}
              onClick={() => onSelect?.(result)}
            />
          ))
        )}
      </div>
    </div>
  );
};
